import {
  Alert, AlertIcon, Badge, Box, Button, Container, Editable, EditableInput,
  EditablePreview, Flex, FormControl, FormLabel, Heading, HStack, IconButton, Image, Input, Menu, MenuButton,
  MenuDivider, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Portal, Progress, Skeleton, Spinner, Stack, Text, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, CopyIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ID3Writer } from 'browser-id3-writer';
import { PictoolsMark } from '../components/PictoolsMark';
import { failArtFor } from '../components/failArt';
import { useAuth } from '../auth/AuthProvider';
import { RootState } from '../redux/store';
import {
  pause as playerPause, play as playerPlay, setQueue, updateTrack, expand, PlayerTrack,
} from '../redux/features/playerSlice';
import {
  useAddSongToCollectionMutation,
  useCreateCollectionMutation,
  useDeleteCollectionMutation,
  useDeleteJobMutation,
  useRenameCollectionMutation,
  useRenameJobMutation,
  useGenerateCoverMutation,
  useRemoveSongFromCollectionMutation,
} from '../redux/services/mixgenie';
import { useCachedSongs } from '../songs/useCachedSongs';
import { mixgenieServiceURL } from '../redux/services/defines';

const ALL = '__all__';

export default function HistoryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const email = user?.email || '';
  const dispatch = useDispatch();

  // Offline-first: reads localStorage cache synchronously so the page paints
  // real rows immediately, then RTK Query refreshes in the background and
  // mirrors the fresh data back to the cache. Sync also fires on window
  // focus + `online` events. See `songs/songsCache.ts`.
  const {
    songs: jobs,
    collections,
    isFirstLoad,
    isFetchError,
    isSyncing,
    isOffline,
    lastSyncedAt,
  } = useCachedSongs(email);

  const [activeCollectionId, setActiveCollectionId] = useState<string>(ALL);

  const [renameJob] = useRenameJobMutation();
  const [deleteJob] = useDeleteJobMutation();
  const [createCollection] = useCreateCollectionMutation();
  const [renameCollection] = useRenameCollectionMutation();
  const [deleteCollection] = useDeleteCollectionMutation();
  const [addSongToCollection] = useAddSongToCollectionMutation();
  const [removeSongFromCollection] = useRemoveSongFromCollectionMutation();

  const visibleJobs = useMemo(() => {
    if (activeCollectionId === ALL) return jobs;
    const active = collections.find((c: any) => String(c._id) === activeCollectionId);
    if (!active) return [];
    const ids = new Set((active.songIds || []).map((x: any) => String(x)));
    return jobs.filter((j) => ids.has(String(j._id)));
  }, [jobs, collections, activeCollectionId]);

  // Playable tracks for the queue — only completed jobs with an audio URL.
  // The row-level index passed to `playAt` must resolve against THIS array,
  // not against `visibleJobs`, so we build it once here and hand each row
  // its own playable-index (or -1 when it's still generating).
  const playableTracks: PlayerTrack[] = useMemo(() => (
    visibleJobs
      .filter((j) => j.status === 'completed' && (j.outputAudioUrl || j.outputUrl))
      .map((j) => ({
        id: String(j._id),
        title: j.title || t('history.untitled', 'Untitled'),
        artist: 'MixGenie',
        audioUrl: j.outputAudioUrl || j.outputUrl,
        coverUrl: j.coverImageUrl || undefined,
        meta: { jobId: String(j._id) },
      }))
  ), [visibleJobs, t]);

  const playIndexById = useMemo(() => {
    const m = new Map<string, number>();
    playableTracks.forEach((t, i) => m.set(t.id, i));
    return m;
  }, [playableTracks]);

  // When a poll returns fresh data for a track currently in the queue,
  // update it in place so the player picks up new URLs / cover art /
  // renamed titles without interrupting playback. Cover is critical:
  // covers are generated AFTER the song completes, so the queue is
  // usually seeded with `coverUrl: undefined` and only gets the real
  // artwork on a later poll — without this patch, the mini + fullscreen
  // players stay stuck on the fallback gradient forever.
  useEffect(() => {
    for (const t of playableTracks) {
      dispatch(updateTrack({
        id: t.id,
        patch: { audioUrl: t.audioUrl, coverUrl: t.coverUrl, title: t.title },
      }));
    }
  }, [playableTracks, dispatch]);

  const playAt = (jobId: string) => {
    const idx = playIndexById.get(jobId);
    if (idx == null || idx < 0) return;
    dispatch(setQueue({ tracks: playableTracks, startIndex: idx }));
  };

  return (
      <Container maxW="720px" py={{ base: 2, md: 4 }} px={0}>
        <Stack spacing={2} mb={5} align="flex-start">
          <HStack spacing={3} align="center">
            <Heading
              as="h1"
              fontSize={{ base: '28px', md: '34px' }}
              fontWeight={800}
              lineHeight="1.1"
              letterSpacing="-0.015em"
              color="whiteAlpha.900"
            >
              {t('history.title', 'My songs')}
            </Heading>
            <SyncPill
              isSyncing={isSyncing}
              isOffline={isOffline}
              isFetchError={isFetchError}
              lastSyncedAt={lastSyncedAt}
              hasCached={jobs.length > 0}
            />
          </HStack>
        </Stack>

        <Flex align="center" justify="flex-end" mb={4}>
          <NewCollectionButton onCreate={(name) => createCollection({ name }).unwrap()} />
        </Flex>

        <PlaylistTabs
          collections={collections}
          activeId={activeCollectionId}
          onSelect={setActiveCollectionId}
          onRename={(id, name) => renameCollection({ id, name }).unwrap()}
          onDelete={async (id) => {
            await deleteCollection({ id }).unwrap();
            if (activeCollectionId === id) setActiveCollectionId(ALL);
          }}
        />

        {isFirstLoad && (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                height="82px"
                borderRadius="14px"
                startColor="rgba(20, 14, 36, 0.8)"
                endColor="rgba(46, 18, 64, 0.8)"
              />
            ))}
          </Stack>
        )}
        {!isFirstLoad && isFetchError && jobs.length === 0 && (
          <Alert
            status="error"
            borderRadius="14px"
            bg="rgba(60, 20, 30, 0.6)"
            backdropFilter="blur(10px)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="red.500"
          >
            <AlertIcon color="red.300" />
            {t('history.loadError', 'Failed to load your songs.')}
          </Alert>
        )}
        {!isFirstLoad && !isFetchError && visibleJobs.length === 0 && (
          <Box
            bg="rgba(20, 14, 36, 0.55)"
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor="whiteAlpha.150"
            borderRadius="18px"
            p={{ base: 6, md: 8 }}
            textAlign="center"
          >
            <Text color="whiteAlpha.700" fontSize={{ base: 'sm', md: 'md' }} mb={4}>
              {activeCollectionId === ALL
                ? t('history.empty', "You haven't created any songs yet.")
                : t('history.emptyPlaylist', 'This playlist is empty. Add songs from All songs.')}
            </Text>
            {activeCollectionId === ALL && (
              <Button
                as={RouterLink}
                to="/generator"
                size="md"
                borderRadius="12px"
                bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
                color="white"
                fontWeight={700}
                _hover={{
                  bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)',
                  transform: 'translateY(-1px)',
                }}
                boxShadow="0 10px 24px rgba(233,74,143,0.28)"
                data-cta="songs:empty-create"
              >
                {t('history.emptyCta', 'Create your first song')}
              </Button>
            )}
          </Box>
        )}

        <Stack spacing={2}>
          {visibleJobs.map((job) => (
            <JobRow
              key={job._id || job.id}
              job={job}
              collections={collections}
              activeCollectionId={activeCollectionId}
              onRename={(title) => renameJob({ jobId: String(job._id), title }).unwrap()}
              onAddToCollection={(collectionId) => addSongToCollection({ collectionId, jobId: String(job._id) }).unwrap()}
              onRemoveFromCollection={(collectionId) => removeSongFromCollection({ collectionId, jobId: String(job._id) }).unwrap()}
              onDelete={() => deleteJob({ jobId: String(job._id) }).unwrap()}
              onPlay={() => playAt(String(job._id))}
              t={t as any}
            />
          ))}
        </Stack>
      </Container>
  );
}

// ---------------------------------------------------------------------------
// Sync / offline indicator
// ---------------------------------------------------------------------------
function SyncPill({
  isSyncing,
  isOffline,
  isFetchError,
  lastSyncedAt,
  hasCached,
}: {
  isSyncing: boolean;
  isOffline: boolean;
  isFetchError: boolean;
  lastSyncedAt: number;
  hasCached: boolean;
}) {
  // Priority: offline > sync-error > syncing > synced
  let label = '';
  let color = 'whiteAlpha.700';
  let dotColor = 'whiteAlpha.500';
  if (isOffline) {
    label = 'Offline';
    color = 'orange.200';
    dotColor = 'orange.300';
  } else if (isFetchError && hasCached) {
    label = 'Sync error';
    color = 'orange.200';
    dotColor = 'orange.300';
  } else if (isSyncing && hasCached) {
    label = 'Syncing';
    color = 'whiteAlpha.700';
    dotColor = 'pink.300';
  } else if (lastSyncedAt) {
    const diff = Date.now() - lastSyncedAt;
    if (diff < 60_000) label = 'Synced';
    else if (diff < 3600_000) label = `Synced ${Math.floor(diff / 60_000)}m ago`;
    else label = `Synced ${Math.floor(diff / 3600_000)}h ago`;
    color = 'whiteAlpha.600';
    dotColor = 'green.300';
  } else {
    return null;
  }
  return (
    <HStack
      spacing={2}
      px={2.5}
      py={1}
      borderRadius="full"
      bg="rgba(20, 14, 36, 0.6)"
      border="1px solid"
      borderColor="whiteAlpha.150"
      fontFamily="ui-monospace, SF Mono, Menlo, monospace"
      fontSize="10px"
      letterSpacing="0.06em"
      textTransform="uppercase"
      color={color}
      aria-live="polite"
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg={dotColor}
        boxShadow={isSyncing ? `0 0 8px currentColor` : undefined}
      />
      <Text as="span">{label}</Text>
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Playlist tabs
// ---------------------------------------------------------------------------
function PlaylistTabs({
  collections,
  activeId,
  onSelect,
  onRename,
  onDelete,
}: {
  collections: any[];
  activeId: string;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => Promise<any>;
  onDelete: (id: string) => Promise<any>;
}) {
  const toast = useToast();
  const managing = useDisclosure();
  return (
    <Box mb={6}>
      <HStack spacing={2} overflowX="auto" pb={2}>
        <TabPill label="All songs" active={activeId === ALL} onClick={() => onSelect(ALL)} />
        {collections.map((c: any) => (
          <TabPill
            key={c._id}
            label={c.name}
            count={c.songIds?.length || 0}
            active={activeId === c._id}
            onClick={() => onSelect(c._id)}
          />
        ))}
        {collections.length > 0 && (
          <IconButton
            aria-label="Manage playlists"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            color="whiteAlpha.700"
            onClick={managing.onOpen}
          />
        )}
      </HStack>
      <Modal isOpen={managing.isOpen} onClose={managing.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>Manage playlists</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              {collections.map((c: any) => (
                <HStack key={c._id} spacing={2}>
                  <Editable
                    defaultValue={c.name}
                    onSubmit={async (v) => {
                      const next = (v || '').trim();
                      if (next && next !== c.name) {
                        try { await onRename(c._id, next); } catch (e: any) {
                          toast({ title: e?.data?.message || 'Rename failed', status: 'error' });
                        }
                      }
                    }}
                    submitOnBlur
                    flex="1"
                  >
                    <EditablePreview px={3} py={2} borderRadius="8px" bg="whiteAlpha.100" w="full" />
                    <EditableInput px={3} py={2} borderRadius="8px" bg="whiteAlpha.100" />
                  </Editable>
                  <IconButton
                    aria-label="Delete playlist"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={async () => {
                      if (!confirm(`Delete playlist "${c.name}"? Songs stay in All songs.`)) return;
                      try { await onDelete(c._id); } catch (e: any) {
                        toast({ title: e?.data?.message || 'Delete failed', status: 'error' });
                      }
                    }}
                  />
                </HStack>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={managing.onClose} variant="ghost" colorScheme="whiteAlpha">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function TabPill({ label, count, active, onClick }: { label: string; count?: number; active: boolean; onClick: () => void }) {
  return (
    <Button
      size="sm"
      onClick={onClick}
      borderRadius="full"
      flexShrink={0}
      fontWeight={active ? 700 : 500}
      px={4}
      bgGradient={active ? 'linear(90deg, #E94A8F 0%, #7354FF 100%)' : undefined}
      bg={active ? undefined : 'rgba(20, 14, 36, 0.55)'}
      color={active ? 'white' : 'whiteAlpha.800'}
      border="1px solid"
      borderColor={active ? 'transparent' : 'whiteAlpha.150'}
      boxShadow={active ? '0 6px 18px rgba(233,74,143,0.28)' : 'none'}
      _hover={{
        bgGradient: active ? 'linear(90deg, #F35DA0 0%, #8567FF 100%)' : undefined,
        bg: active ? undefined : 'rgba(30, 20, 48, 0.7)',
        borderColor: active ? 'transparent' : 'whiteAlpha.300',
      }}
    >
      {label}
      {count != null && (
        <Text as="span" ml={2} fontSize="xs" opacity={0.8}>
          {count}
        </Text>
      )}
    </Button>
  );
}

function NewCollectionButton({ onCreate }: { onCreate: (name: string) => Promise<any> }) {
  const toast = useToast();
  const modal = useDisclosure();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button size="sm" leftIcon={<AddIcon />} colorScheme="pink" variant="outline" onClick={modal.onOpen}>
        New playlist
      </Button>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>New playlist</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="e.g. Chill vibes"
              value={name}
              onChange={(e) => setName(e.target.value)}
              bg="whiteAlpha.100"
              borderColor="whiteAlpha.300"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="pink"
              isLoading={busy}
              isDisabled={!name.trim()}
              onClick={async () => {
                setBusy(true);
                try {
                  await onCreate(name.trim());
                  setName('');
                  modal.onClose();
                } catch (e: any) {
                  toast({ title: e?.data?.message || 'Create failed', status: 'error' });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Single song row — SoundCloud-style
// ---------------------------------------------------------------------------
function JobRow({
  job,
  collections,
  activeCollectionId,
  onRename,
  onAddToCollection,
  onRemoveFromCollection,
  onDelete,
  onPlay,
  t,
}: {
  job: any;
  collections: any[];
  activeCollectionId: string;
  onRename: (title: string) => Promise<any>;
  onAddToCollection: (collectionId: string) => Promise<any>;
  onRemoveFromCollection: (collectionId: string) => Promise<any>;
  onDelete: () => Promise<any>;
  onPlay: () => void;
  t: (k: string, d?: string) => string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jobId = String(job._id);
  const audioUrl: string | undefined = job.outputAudioUrl || job.outputUrl;
  const createdAt = job.createdAt ? new Date(job.createdAt) : null;
  const displayTitle = job.title || t('history.untitled', 'Untitled');

  const player = useSelector((s: RootState) => s.player);
  const currentTrackId = player.index >= 0 ? player.queue[player.index]?.id : null;
  const isCurrent = currentTrackId === jobId;
  const isPlaying = isCurrent && player.isPlaying;
  const progressPct = isCurrent && player.duration > 0
    ? (player.progress / player.duration) * 100
    : 0;
  // Session-only flag — set by GlobalAudioPlayer when this track failed
  // to load twice in a row (URL dead, file gone, decode error). The row
  // renders greyed and the auto-play flow skips it on next().
  const isUnavailable = player.unavailableIds.includes(jobId);

  const canPlay = job.status === 'completed' && !!audioUrl && !isUnavailable;

  const handleTogglePlay = () => {
    if (!canPlay) return;
    if (isCurrent) {
      dispatch(isPlaying ? playerPause() : playerPlay());
    } else {
      onPlay();
    }
  };

  // Double-click / double-tap: play + open the full-screen player. This
  // is the standard media-app pattern (Spotify, Apple Music) — single
  // tap toggles playback in the row, double tap opens the immersive view.
  const handleOpenFull = () => {
    if (!canPlay) return;
    if (!isCurrent) onPlay();
    dispatch(expand());
  };

  const goGenerateSimilar = () => {
    const params = new URLSearchParams();
    if (job.referenceTitle) params.set('referenceMode', 'title-artist');
    if (job.referenceTitle) params.set('referenceTitle', job.referenceTitle);
    if (job.referenceArtist) params.set('referenceArtist', job.referenceArtist);
    if (job.referenceSlug) params.set('referenceSlug', job.referenceSlug);
    if (job.referenceArtistSlug) params.set('referenceArtistSlug', job.referenceArtistSlug);
    if (job.prompt) params.set('vibe', job.prompt);
    else if (job.vibe) params.set('vibe', job.vibe);
    navigate(`/generator?${params.toString()}`);
  };

  const inCollectionsForSong = collections.filter((c: any) =>
    (c.songIds || []).some((sid: any) => String(sid) === jobId),
  );

  const downloadWithTags = async () => {
    if (!audioUrl) return;
    // Fetch via the BE proxy — that's what handles the CORS + forces the
    // filename to the CURRENT title (renames flow through) + refreshes stale
    // Mureka signed URLs. Then rewrite ID3 tags in the browser and download.
    const proxyUrl = `${mixgenieServiceURL}/generation-jobs/${jobId}/audio.mp3`;
    try {
      const res = await fetch(proxyUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`fetch failed ${res.status}`);
      const buf = await res.arrayBuffer();
      const writer = new ID3Writer(buf);
      writer.setFrame('TIT2', displayTitle).setFrame('TPE1', ['MixGenie']).setFrame('TALB', 'MixGenie');
      writer.addTag();
      const blob = writer.getBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(displayTitle)}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: t('history.downloadFallback', 'Downloading direct — tags may be Mureka defaults.'), status: 'warning', duration: 2500 });
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = `${sanitizeFilename(displayTitle)}.mp3`;
      a.click();
    }
  };

  const [generateCoverMut, { isLoading: coverBusy }] = useGenerateCoverMutation();
  const regenerateCover = async () => {
    try {
      await generateCoverMut({ jobId }).unwrap();
      toast({ title: t('history.coverDone', 'Cover updated'), status: 'success', duration: 2000 });
    } catch (e: any) {
      toast({ title: e?.data?.message || t('history.coverFail', 'Cover generation failed — try again'), status: 'error' });
    }
  };

  const renameModal = useDisclosure();
  const lyricsModal = useDisclosure();
  const hasLyrics = !!(job.lyrics && String(job.lyrics).trim());
  const copyLyrics = async () => {
    if (!hasLyrics) return;
    try {
      await navigator.clipboard.writeText(String(job.lyrics));
      toast({ title: t('history.lyricsCopied', 'Lyrics copied'), status: 'success', duration: 1500 });
    } catch {
      toast({ title: t('history.lyricsCopyFail', 'Copy failed — select the text manually'), status: 'warning', duration: 2500 });
    }
  };
  const [renameDraft, setRenameDraft] = useState(displayTitle);
  const [renameBusy, setRenameBusy] = useState(false);
  const openRename = () => { setRenameDraft(displayTitle); renameModal.onOpen(); };
  const submitRename = async () => {
    const next = renameDraft.trim();
    if (!next || next === displayTitle) { renameModal.onClose(); return; }
    setRenameBusy(true);
    try {
      await onRename(next);
      renameModal.onClose();
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Rename failed', status: 'error' });
    } finally {
      setRenameBusy(false);
    }
  };

  return (
    <Box
      as={canPlay ? 'button' : 'div'}
      w="full"
      textAlign="left"
      onClick={canPlay ? handleTogglePlay : undefined}
      onDoubleClick={canPlay ? handleOpenFull : undefined}
      bg={isUnavailable
        ? 'rgba(20, 14, 36, 0.35)'
        : isCurrent ? 'rgba(60, 30, 80, 0.75)' : 'rgba(20, 14, 36, 0.55)'}
      backdropFilter="blur(14px)"
      border="1px solid"
      borderColor={isUnavailable
        ? 'whiteAlpha.100'
        : isCurrent ? 'pink.400' : 'whiteAlpha.150'}
      borderRadius="16px"
      px={{ base: 3, md: 4 }}
      py={3}
      opacity={isUnavailable ? 0.55 : 1}
      transition="background 0.15s, border-color 0.15s, opacity 0.15s"
      cursor={canPlay ? 'pointer' : 'default'}
      boxShadow={isCurrent
        ? '0 12px 32px rgba(233,74,143,0.25)'
        : '0 4px 14px rgba(0,0,0,0.15)'}
      _hover={{
        bg: isUnavailable
          ? 'rgba(20, 14, 36, 0.45)'
          : isCurrent ? 'rgba(60, 30, 80, 0.85)' : 'rgba(30, 20, 48, 0.7)',
        borderColor: isUnavailable
          ? 'whiteAlpha.150'
          : isCurrent ? 'pink.300' : 'whiteAlpha.300',
      }}
      _focus={{ outline: '2px solid', outlineColor: 'pink.400', outlineOffset: '2px' }}
    >
      <Flex align="center" gap={3}>
        {/* Cover art with always-visible play/pause icon overlay */}
        <Box position="relative" flexShrink={0}>
          <Box
            boxSize={{ base: '52px', md: '60px' }}
            borderRadius="8px"
            bgGradient={isCurrent
              ? 'linear(135deg, pink.400, purple.500)'
              : 'linear(135deg, pink.500, purple.600)'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize="2xl"
            opacity={canPlay ? 1 : 0.55}
            overflow="hidden"
          >
            {job.status === 'failed'
              ? <Image src={failArtFor(jobId)} boxSize="full" objectFit="cover" alt={t('history.failedArt', 'This job has failed')} draggable={false} style={{ opacity: 1 }} />
              : job.coverImageUrl
              ? <Image src={job.coverImageUrl} boxSize="full" objectFit="cover" alt="" draggable={false} />
              : job.status === 'completed'
                ? (
                  <Box
                    as="button"
                    onClick={(e: any) => { e.stopPropagation(); if (!coverBusy) regenerateCover(); }}
                    boxSize="full"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="2px"
                    bg="rgba(10, 8, 20, 0.55)"
                    _hover={{ bg: 'rgba(10, 8, 20, 0.75)' }}
                    title={t('history.coverCta', 'Generate cover with pictools.ai (1 credit)')}
                    aria-label={t('history.coverCta', 'Generate cover with pictools.ai (1 credit)')}
                  >
                    {coverBusy ? <Spinner size="xs" color="pink.300" /> : <PictoolsMark size={20} />}
                    <Text fontSize="8px" lineHeight="1.1" color="whiteAlpha.800" fontWeight={700}>
                      {coverBusy ? t('history.coverBusyShort', '…') : t('history.coverCtaShort', '+ cover')}
                    </Text>
                  </Box>
                )
                : (job.instrumental ? '🎹' : '🎵')}
          </Box>
          {canPlay && (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg="blackAlpha.500"
              borderRadius="8px"
              color="white"
              opacity={isCurrent ? 1 : 0.85}
              pointerEvents="none"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d={isPlaying ? 'M6 5h4v14H6zM14 5h4v14h-4z' : 'M8 5v14l11-7z'} />
              </svg>
            </Flex>
          )}
        </Box>

        {/* Title + meta */}
        <Box flex="1" minW={0}>
          <Text
            fontSize="md"
            fontWeight={700}
            color="whiteAlpha.900"
            noOfLines={1}
          >
            {displayTitle}
          </Text>
          <HStack spacing={2} color="whiteAlpha.600" fontSize="xs" mt={0.5} wrap="wrap">
            <Text noOfLines={1}>MixGenie</Text>
            {createdAt && <Text>· {createdAt.toLocaleDateString()}</Text>}
            {isUnavailable && (
              <Badge colorScheme="orange" fontSize="9px" textTransform="uppercase" letterSpacing="0.06em">
                {t('history.unavailable', 'Unavailable')}
              </Badge>
            )}
            {job.instrumental && <Badge colorScheme="purple" fontSize="9px">instrumental</Badge>}
            {(job.referenceTitle || job.referenceArtist) && (
              <Text as="span" noOfLines={1} color="whiteAlpha.500">
                · from {job.referenceTitle}{job.referenceArtist ? ` — ${job.referenceArtist}` : ''}
              </Text>
            )}
            {job.status === 'processing' && (
              <HStack spacing={1} color="yellow.300">
                <Spinner size="xs" />
                <Text>{t('history.processing', 'Generating…')}</Text>
              </HStack>
            )}
            {job.status === 'failed' && (
              <Badge colorScheme="red" fontSize="9px">failed</Badge>
            )}
          </HStack>
          {/* Live progress bar — only for the currently playing track */}
          {isCurrent && (
            <Progress
              value={progressPct}
              size="xs"
              colorScheme="pink"
              bg="whiteAlpha.200"
              borderRadius="full"
              mt={2}
            />
          )}
        </Box>

        {/* Actions — stop click propagation so pressing the menu / download
            button doesn't also trigger the row's tap-to-play. */}
        <HStack spacing={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          {canPlay && (
            <IconButton
              aria-label="Download"
              onClick={(e) => { e.stopPropagation(); downloadWithTags(); }}
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'pink.300', bg: 'whiteAlpha.100' }}
              size="sm"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z" />
                </svg>
              }
            />
          )}
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="More"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'pink.300', bg: 'whiteAlpha.100' }}
              size="sm"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                </svg>
              }
            />
            <Portal>
            <MenuList bg="night.800" borderColor="whiteAlpha.300" minW="240px" zIndex={2000}>
              <MenuItem
                bg="night.800"
                color="whiteAlpha.900"
                _hover={{ bg: 'whiteAlpha.200' }}
                _focus={{ bg: 'whiteAlpha.200' }}
                onClick={openRename}
                icon={<EditIcon />}
              >
                {t('history.rename', 'Rename')}
              </MenuItem>
              {hasLyrics && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={lyricsModal.onOpen}
                  icon={<Text as="span">📝</Text>}
                >
                  {t('history.viewLyrics', 'View lyrics')}
                </MenuItem>
              )}
              {canPlay && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={goGenerateSimilar}
                  icon={<Text as="span">✨</Text>}
                >
                  {t('history.generateSimilar', 'Generate similar')}
                </MenuItem>
              )}
              {job.status === 'completed' && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  isDisabled={coverBusy}
                  onClick={regenerateCover}
                >
                  <HStack spacing={1.5} as="span">
                    <PictoolsMark size={13} />
                    <Text as="span">
                      {coverBusy
                        ? t('history.coverBusy', 'Painting cover…')
                        : job.coverImageUrl
                          ? t('history.coverRegen', 'Regenerate cover with pictools.ai (1 credit)')
                          : t('history.coverCta', 'Generate cover with pictools.ai (1 credit)')}
                    </Text>
                  </HStack>
                </MenuItem>
              )}
              {canPlay && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={downloadWithTags}
                  icon={<Text as="span">⬇</Text>}
                >
                  {t('history.download', 'Download')}
                </MenuItem>
              )}
              <MenuDivider borderColor="whiteAlpha.200" />
              <Box px={3} py={1}>
                <Text fontSize="xs" color="whiteAlpha.500" textTransform="uppercase">
                  {t('history.playlists', 'Playlists')}
                </Text>
              </Box>
              {collections.length === 0 && (
                <Box px={3} py={2}>
                  <Text fontSize="xs" color="whiteAlpha.600">{t('history.noPlaylists', 'No playlists yet.')}</Text>
                </Box>
              )}
              {collections.map((c: any) => {
                const inThis = inCollectionsForSong.some((x: any) => String(x._id) === String(c._id));
                return (
                  <MenuItem
                    key={c._id}
                    bg="night.800"
                    color="whiteAlpha.900"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    _focus={{ bg: 'whiteAlpha.200' }}
                    onClick={async () => {
                      try {
                        if (inThis) await onRemoveFromCollection(c._id);
                        else await onAddToCollection(c._id);
                      } catch (e: any) {
                        toast({ title: e?.data?.message || 'Update failed', status: 'error' });
                      }
                    }}
                  >
                    {inThis ? '✓ ' : ''}{c.name}
                  </MenuItem>
                );
              })}
              {activeCollectionId !== ALL && inCollectionsForSong.some((x: any) => String(x._id) === activeCollectionId) && (
                <>
                  <MenuDivider borderColor="whiteAlpha.200" />
                  <MenuItem
                    bg="night.800"
                    color="red.300"
                    _hover={{ bg: 'red.900' }}
                    _focus={{ bg: 'red.900' }}
                    onClick={async () => {
                      try { await onRemoveFromCollection(activeCollectionId); } catch (e: any) {
                        toast({ title: e?.data?.message || 'Remove failed', status: 'error' });
                      }
                    }}
                  >
                    Remove from this playlist
                  </MenuItem>
                </>
              )}
              <MenuDivider borderColor="whiteAlpha.200" />
              <MenuItem
                icon={<DeleteIcon />}
                bg="night.800"
                color="red.300"
                _hover={{ bg: 'red.900' }}
                _focus={{ bg: 'red.900' }}
                onClick={async () => {
                  const ok = window.confirm(
                    t('history.confirmDelete', `Delete "${displayTitle}"? This removes the song and its audio permanently.`),
                  );
                  if (!ok) return;
                  try {
                    await onDelete();
                    toast({ title: t('history.deleted', 'Song deleted'), status: 'success', duration: 2000 });
                  } catch (e: any) {
                    toast({ title: e?.data?.message || 'Delete failed', status: 'error' });
                  }
                }}
              >
                {t('history.delete', 'Delete song')}
              </MenuItem>
            </MenuList>
            </Portal>
          </Menu>
        </HStack>
      </Flex>

      {job.status === 'failed' && job.errorMessage && (
        <Alert status="error" borderRadius="8px" mt={3} bg="red.900" color="whiteAlpha.900" borderWidth="1px" borderColor="red.500" fontSize="sm" onClick={(e) => e.stopPropagation()}>
          <AlertIcon color="red.300" />
          {job.errorMessage}
        </Alert>
      )}

      <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900" onClick={(e) => e.stopPropagation()}>
          <ModalHeader>{t('history.renameTitle', 'Rename song')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="whiteAlpha.700">
                {t('history.renameLabel', 'Song title')}
              </FormLabel>
              <Input
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="whiteAlpha" onClick={renameModal.onClose} mr={2}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="pink"
              onClick={submitRename}
              isLoading={renameBusy}
              isDisabled={!renameDraft.trim() || renameDraft.trim() === displayTitle}
            >
              {t('save', 'Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={lyricsModal.isOpen} onClose={lyricsModal.onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900" onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Stack spacing={0}>
              <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.08em">
                {t('history.lyricsFor', 'Lyrics')}
              </Text>
              <Text fontSize="md" color="whiteAlpha.900" noOfLines={2}>{displayTitle}</Text>
            </Stack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              as="pre"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              fontFamily="body"
              fontSize={{ base: 'sm', md: 'md' }}
              lineHeight="1.7"
              color="whiteAlpha.900"
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.150"
              borderRadius="10px"
              p={4}
              m={0}
            >
              {job.lyrics}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<CopyIcon />}
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={copyLyrics}
              mr={2}
            >
              {t('history.copyLyrics', 'Copy')}
            </Button>
            <Button colorScheme="pink" onClick={lyricsModal.onClose}>
              {t('close', 'Close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function sanitizeFilename(name: string): string {
  return (name || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80).trim() || 'song';
}
